import * as React from 'react';
import { useMemo, memo, forwardRef } from 'react';
import { cn, OptionsType, filterIconMap } from '@restworld/utils-common';

interface IChipCheckBoxProps extends OptionsType {
  onChangeHandler?: (x: string) => void;
  checked: boolean;
  testId?: string;
}
export const ChipCheckBox = memo((props: IChipCheckBoxProps) => {
  const { id, icon, label, checked, onChangeHandler, testId, ...rest } = props;
  const Icon = filterIconMap[icon as keyof typeof filterIconMap];
  const iconEl = useMemo(
    () =>
      Icon ? (
        typeof Icon === 'string' ? (
          <IconSrc src={Icon} alt={label} />
        ) : (
          <IconWrapper isActive={checked}>
            <Icon size={24} stroke={checked ? 'white' : 'black'} />{' '}
          </IconWrapper>
        )
      ) : null,
    [Icon, checked, label]
  );
  const inputEl = useMemo(
    () => (
      <CheckboxInput
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          if (onChangeHandler) onChangeHandler(id);
        }}
        {...rest}
      />
    ),
    [checked, id, onChangeHandler, rest]
  );
  return (
    <StyledChipCheckbox data-cy={testId} key={id} checked={checked}>
      {iconEl}
      {inputEl}
      <span className="font-normal text-sm">{label}</span>
    </StyledChipCheckbox>
  );
});
export const ChipCheckboxGroup = forwardRef(
  (
    {
      options,
      onChangeHandler,
      value,
      customChipGroupStyles,
      testId,
      ...rest
    }: {
      options: OptionsType[];
      value: string[];
      onChangeHandler?: (x: string) => void;
      customChipGroupStyles?: string;
      testId?: string;
    },
    ref?: React.Ref<HTMLInputElement>
  ) => (
    <ChipGroupStyled data-cy={testId} customStyles={customChipGroupStyles} ref={ref}>
      {options?.map((chip, index) => {
        return (
          <ChipCheckBox
            key={index.toString()}
            testId={`${chip.key}`}
            {...rest}
            {...{ ...chip, onChangeHandler }}
            checked={Array.isArray(value) ? value?.indexOf(chip.id) > -1 : value === chip.id}
          />
        );
      })}
    </ChipGroupStyled>
  )
);
const StyledChipCheckbox = ({
  checked,
  className,
  children,
  ...props
}: { checked: boolean; className?: string; children?: React.ReactNode } & React.HTMLAttributes<HTMLLabelElement>) => (
  <label
    className={cn(
      'inline-flex gap-2 items-center rounded-lg py-2 px-4 cursor-pointer mr-2 border border-gray-400',
      checked ? 'bg-primary-800 text-white' : 'bg-white text-gray-600',
      className
    )}
    {...props}
  >
    {children}
  </label>
);
const ChipGroupStyled = React.forwardRef<
  HTMLDivElement,
  { customStyles?: string; className?: string; children?: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>
>(({ customStyles, className, children, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-row gap-2 flex-wrap', customStyles, className)} {...props}>
    {children}
  </div>
));
const CheckboxInput = ({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input className={cn('hidden', className)} {...props} />
);
const IconSrc = ({ className, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => (
  <img className={cn('w-4 h-4 mr-2', className)} {...props} />
);
const IconWrapper = ({
  isActive,
  className,
  children,
  ...props
}: { isActive: boolean; className?: string; children?: React.ReactNode } & React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn('h-4 w-4 rounded-lg flex justify-center items-center', isActive ? 'bg-primary-800' : '', className)}
    {...props}
  >
    {children}
  </span>
);
